import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import {
    SPAWN_PATTERN_KINDS,
    SPAWN_PATTERN_LABELS,
    createSpawnPattern,
} from "../../utils/svgDiagram2DModel";
import type {
    DiagramNode,
    SpawnPatternKind,
} from "../../utils/svgDiagram2DModel";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/** Spawn-pattern editor for a host node (not spawn clones). */
export function SvgDiagram2DNodeSpawnSection({ node }: { node: DiagramNode }) {
    const { doc, setNodeSpawn, updateNodeSpawn } = useSvgDiagram2D();

    if (node.spawnedFrom) return null;

    const templateOptions = doc.nodes.filter(
        (n) => n.id !== node.id && !n.spawnedFrom,
    );

    const enabled = node.spawn != null;
    const spawn = node.spawn;

    return (
        <div style={{ marginTop: 6 }}>
            <p className="path-tool-meta" style={{ margin: "0 0 4px", opacity: 0.7 }}>
                Spawn clones
            </p>
            <CheckboxField
                checked={enabled}
                onChange={(checked) => {
                    if (!checked) {
                        setNodeSpawn(node.id, null);
                        return;
                    }
                    const templateId = templateOptions[0]?.id;
                    if (!templateId) return;
                    setNodeSpawn(
                        node.id,
                        createSpawnPattern("square", templateId),
                    );
                }}
                label="Surround with clones of another node"
                inline
            />
            {enabled && spawn && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        marginTop: 6,
                    }}
                >
                    <label className="field-label">
                        Template node
                        <select
                            className="anchor-select"
                            value={spawn.templateId}
                            onChange={(e) => {
                                updateNodeSpawn(node.id, {
                                    templateId: e.target.value,
                                });
                            }}
                            disabled={templateOptions.length === 0}
                        >
                            {templateOptions.map((n) => (
                                <option key={n.id} value={n.id}>
                                    {n.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="field-label">
                        Pattern
                        <select
                            className="anchor-select"
                            value={spawn.kind}
                            onChange={(e) => {
                                const kind = e.target.value as SpawnPatternKind;
                                setNodeSpawn(
                                    node.id,
                                    createSpawnPattern(kind, spawn.templateId),
                                );
                            }}
                        >
                            {SPAWN_PATTERN_KINDS.map((k) => (
                                <option key={k} value={k}>
                                    {SPAWN_PATTERN_LABELS[k]}
                                </option>
                            ))}
                        </select>
                    </label>
                    {spawn.kind === "square" ? (
                        <div className="field-row field-row--two">
                            <label className="field-label">
                                Per side
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={spawn.countPerSide}
                                    onChange={(v) => {
                                        updateNodeSpawn(node.id, {
                                            countPerSide: Math.max(
                                                1,
                                                Math.floor(num(v, 3)),
                                            ),
                                        });
                                    }}
                                />
                            </label>
                            <label className="field-label">
                                Gap
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={spawn.gap}
                                    onChange={(v) => {
                                        updateNodeSpawn(node.id, {
                                            gap: Math.max(0, num(v, 20)),
                                        });
                                    }}
                                />
                            </label>
                        </div>
                    ) : (
                        <div className="field-row field-row--two">
                            <label className="field-label">
                                Count
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={spawn.count}
                                    onChange={(v) => {
                                        updateNodeSpawn(node.id, {
                                            count: Math.max(
                                                1,
                                                Math.floor(num(v, 8)),
                                            ),
                                        });
                                    }}
                                />
                            </label>
                            <label className="field-label">
                                Radius
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={spawn.radius}
                                    onChange={(v) => {
                                        updateNodeSpawn(node.id, {
                                            radius: Math.max(0, num(v, 60)),
                                        });
                                    }}
                                />
                            </label>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
